/**
 * Browser web-push subscription helpers (Phase 2 notifications).
 *
 * Client-only module — uses ``navigator.serviceWorker``,
 * ``PushManager``, ``Notification`` and so cannot be imported from a
 * Server Component. The settings page imports it from its 'use client'
 * tree.
 *
 * Design notes:
 * - The single ``getPushCapability()`` deliberately does NOT carry an
 *   ``iosVersionTooOld`` / ``iosPwaRequired`` field — UA-based version
 *   sniffing is unreliable (macOS Add-to-Dock also reports
 *   display-mode: standalone) and the cleanest failure mode is to let
 *   ``pushManager.subscribe()`` throw, then surface a single
 *   ``pushSubscribeError`` toast. Decision #8 in plan.md.
 * - Every helper is idempotent: re-subscribing returns the same row,
 *   unsubscribing a missing endpoint is ok, etc. The settings toggle
 *   can call these multiple times during a single user click without
 *   leaking duplicate subscriptions.
 */

import { getApiBaseUrl } from '@/lib/api-config'
import type { GetAuthToken } from '@/lib/backend-auth-token'
import { getBackendAuthToken } from '@/lib/backend-auth-token'
import { getVapidPublicKey } from '@/lib/vapid-key'

const SW_URL = '/sw.js'
const SW_SCOPE = '/'

export type PushCapability = {
  supported: boolean
  permission: NotificationPermission
}

export function getPushCapability(): PushCapability {
  if (typeof window === 'undefined') return { supported: false, permission: 'default' }
  const supported =
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  const permission = 'Notification' in window ? Notification.permission : 'default'
  return { supported, permission }
}

async function ensureRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration(SW_SCOPE)
  if (existing) return existing
  return navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE })
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (typeof window === 'undefined') return null
  if (!('serviceWorker' in navigator)) return null
  const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE)
  if (!reg) return null
  return reg.pushManager.getSubscription()
}

export async function subscribeToPush(
  getToken: GetAuthToken,
): Promise<PushSubscription> {
  if (!getPushCapability().supported) {
    throw new Error('Web push not supported in this browser')
  }
  // Notification.requestPermission resolves synchronously in modern browsers
  // when the user has already granted; otherwise it shows the OS prompt.
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error(`Notification permission ${permission}`)
  }

  const reg = await ensureRegistration()
  await navigator.serviceWorker.ready

  // pushManager.subscribe is idempotent at the browser level: a second
  // call with matching applicationServerKey returns the existing
  // subscription rather than minting a new one.
  const vapidKey = await getVapidPublicKey()
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    // Cast: pushManager.subscribe wants BufferSource with strict
    // ArrayBuffer backing; the Uint8Array we hand it satisfies the
    // runtime contract but TS narrows ``buffer`` to ArrayBufferLike.
    applicationServerKey: vapidKey as BufferSource,
  })

  // If the backend POST fails, roll the browser subscription back so
  // the user isn't left with a zombie SW subscription that diverges
  // from server prefs (Bugbot PR #1642 BB7). Without this the next
  // push from a previously-recorded device would still arrive; this
  // device would never get any.
  try {
    await postSubscription(getToken, sub)
  } catch (err) {
    try {
      await sub.unsubscribe()
    } catch {
      // best-effort cleanup; surface the original error to the caller
    }
    throw err
  }
  return sub
}

export async function unsubscribeFromPush(getToken: GetAuthToken): Promise<void> {
  const sub = await getCurrentSubscription()
  if (!sub) return
  // Tell the browser first so the SW stops receiving — even if the
  // backend DELETE fails, the local browser cannot receive further
  // pushes, which is the user's expectation when they toggle off.
  try {
    await sub.unsubscribe()
  } catch {
    // ignore — subscription may already be invalidated
  }
  await deleteSubscription(getToken, sub.endpoint)
}

// ---------------------------------------------------------------------------
// Backend calls
// ---------------------------------------------------------------------------

async function postSubscription(
  getToken: GetAuthToken,
  sub: PushSubscription,
): Promise<void> {
  const token = await getBackendAuthToken(getToken)
  if (!token) throw new Error('Not authenticated')
  const json = sub.toJSON()
  const body = {
    endpoint: sub.endpoint,
    keys: {
      p256dh: json.keys?.p256dh ?? '',
      auth: json.keys?.auth ?? '',
    },
    userAgent: navigator.userAgent,
  }
  const resp = await fetch(`${getApiBaseUrl()}/api/users/me/push-subscriptions`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
  if (!resp.ok) {
    throw new Error(`Push subscribe failed (${resp.status})`)
  }
}

async function deleteSubscription(
  getToken: GetAuthToken,
  endpoint: string,
): Promise<void> {
  const token = await getBackendAuthToken(getToken)
  if (!token) return // best-effort; nothing we can do unauthenticated
  const url = new URL(
    `${getApiBaseUrl()}/api/users/me/push-subscriptions/by-endpoint`,
  )
  url.searchParams.set('endpoint', endpoint)
  await fetch(url.toString(), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  }).catch(() => {
    // ignore — best-effort cleanup
  })
}
